import type { Profile } from "@codeshare/session";
import {
  type FileEntry,
  forgetDocument,
  type Persistence,
  persistDocument,
  type RoomLike,
  type RunSessionInfo,
  RunSessions,
  Workspace,
  YjsChannelProvider,
} from "@codeshare/sync";
import { Awareness } from "y-protocols/awareness";
import * as Y from "yjs";

export interface WorkspaceSnapshot {
  files: { id: string; path: string }[];
  activeId: string | undefined;
  /** False when a newer build wrote this document (read-only until refresh). */
  canWrite: boolean;
  /** The room's toolchain choices by runner (e.g. { ocaml: "oxcaml" }); unvalidated. */
  toolchains: Record<string, string>;
}

export interface WorkspaceSessionOptions {
  profile: Profile;
  /** Opened when there's nothing to show (new solo session or empty room). */
  seed?: { path: string; content: string };
  /**
   * Keep the solo workspace in IndexedDB, so a reload (or leaving a room) doesn't lose work. The
   * seed is then only used when nothing was stored.
   */
  persistLocally?: boolean;
}

/** Storage name of the solo (not in a room) workspace. Room topics are hex, so this can't clash. */
export const LOCAL_TOPIC = "local";

/** How long a joiner waits for peers to send the room's files before creating one itself. */
const EMPTY_ROOM_GRACE_MS = 3_000;

/**
 * The shared workspace behind the editor: a Y.Doc (solo or synced with a room), its awareness,
 * the room provider and persistence, and which file this user has open.
 */
export class WorkspaceSession {
  doc = new Y.Doc();
  workspace = new Workspace(this.doc);
  awareness = new Awareness(this.doc);
  readonly #options: WorkspaceSessionOptions;
  readonly #listeners = new Set<() => void>();
  #activeId: string | undefined;
  #provider: YjsChannelProvider | undefined;
  #persistence: Persistence | undefined;
  #topic: string | undefined;
  #unobserve: () => void = () => {};
  #emptyTimer: ReturnType<typeof setTimeout> | undefined;
  /** The file made for a room that looked empty; gives way if the room's files arrive late. */
  #placeholderId: string | undefined;
  #snapshot: WorkspaceSnapshot;
  /** Run sessions of the current document (replaced with it). */
  #runSessions!: RunSessions;
  #sessionList: RunSessionInfo[] = [];
  readonly #sessionListeners = new Set<() => void>();
  /** While in a room you created: the solo workspace's storage, kept in step with the room. */
  #localMirror: { destroy(): Promise<void> | void } | undefined;

  constructor(options: WorkspaceSessionOptions) {
    this.#options = options;
    this.#reset(new Y.Doc());
    this.#snapshot = this.#takeSnapshot();
    if (options.persistLocally && options.seed) {
      this.ready = this.#restoreLocal(options.seed);
      return;
    }
    this.workspace.migrate();
    if (options.seed)
      this.#activeId = this.workspace.create({
        ...options.seed,
        createdBy: options.profile.clientId,
      });
    this.#snapshot = this.#takeSnapshot();
  }

  /** Resolves once the solo workspace has been restored (or seeded). */
  ready: Promise<void> = Promise.resolve();

  /** Opens the stored solo workspace, or the seed if there's none. */
  async #restoreLocal(seed: { path: string; content: string }): Promise<void> {
    const doc = this.doc;
    this.#persistence = persistDocument(doc, LOCAL_TOPIC);
    await this.#persistence.whenLoaded;
    if (this.doc !== doc || this.#topic) return; // joined a room meanwhile
    this.workspace.migrate();
    if (this.workspace.list().length === 0 && this.workspace.canWrite) {
      this.#activeId = this.workspace.create({
        ...seed,
        createdBy: this.#options.profile.clientId,
      });
    }
    this.#pickActive();
    this.#emit();
  }

  get snapshot(): WorkspaceSnapshot {
    return this.#snapshot;
  }

  get active(): FileEntry | undefined {
    return this.#activeId ? this.workspace.get(this.#activeId) : undefined;
  }

  get topic(): string | undefined {
    return this.#topic;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /** The room's run sessions (terminal streaming). */
  get runSessions(): RunSessions {
    return this.#runSessions;
  }

  /** Live sessions, as a stable array between changes (for React). */
  get sessionList(): RunSessionInfo[] {
    return this.#sessionList;
  }

  subscribeSessions(listener: () => void): () => void {
    this.#sessionListeners.add(listener);
    return () => this.#sessionListeners.delete(listener);
  }

  setProfile(profile: Profile): void {
    this.awareness.setLocalStateField("user", {
      name: profile.name,
      color: profile.color,
      clientId: profile.clientId,
    });
  }

  open(id: string): void {
    if (!this.workspace.get(id) || id === this.#activeId) return;
    this.#activeId = id;
    this.#emit();
  }

  /** Adds a file and opens it. Name clashes get a numeric suffix ("main (2).py"). */
  addFile(path: string, content = ""): string {
    const id = this.workspace.create({
      path: this.#uniquePath(path),
      content,
      createdBy: this.#options.profile.clientId,
    });
    this.#activeId = id;
    this.#emit();
    return id;
  }

  /**
   * Adds many files at once (one shared change), with unique names; opens the first. Returns the
   * paths actually used.
   */
  importFiles(files: { path: string; text: string }[]): string[] {
    const paths: string[] = [];
    let first: string | undefined;
    this.doc.transact(() => {
      for (const file of files) {
        const path = this.#uniquePath(file.path);
        const id = this.workspace.create({
          path,
          content: file.text,
          createdBy: this.#options.profile.clientId,
        });
        first ??= id;
        paths.push(path);
      }
    });
    if (first) {
      this.#activeId = first;
      this.#emit();
    }
    return paths;
  }

  /** Every file's path and text (for export). */
  contents(): { path: string; text: string }[] {
    return this.workspace.list().map((f) => ({ path: f.path, text: f.text.toString() }));
  }

  /** Deletes a file for everyone; another file opens if it was the open one. */
  delete(id: string): void {
    this.workspace.delete(id);
  }

  /** Moves a file to another tab position, for everyone. */
  move(id: string, index: number): void {
    this.workspace.move(id, index);
  }

  /** Sets the room's toolchain for a runner (undefined: its default). */
  setToolchain(runnerId: string, toolchain: string | undefined): void {
    if (this.workspace.canWrite) this.workspace.setToolchain(runnerId, toolchain);
  }

  rename(id: string, path: string): void {
    const unique = this.workspace.findByPath(path)?.id === id ? path : this.#uniquePath(path);
    this.workspace.rename(id, unique);
  }

  /**
   * Shares the current document with a room (the creator's case): keeps the content, persists it
   * under the room topic, and starts syncing. It's still your own workspace too: changes keep
   * being saved as the solo workspace, so a fresh visit (a new room) starts from the latest.
   */
  async shareWith(room: RoomLike, topic: string): Promise<void> {
    // The stored solo workspace (or the seed) first: rooms start as soon as the page opens.
    await this.ready;
    const local = this.#options.persistLocally ? this.#persistence : undefined;
    this.#persistence = undefined; // kept alive past #connect's disconnect
    await this.#connect(room, topic);
    this.#localMirror = local;
  }

  /**
   * Joins a room's document (the joiner's case): starts from what's stored locally for the room
   * (if anything), then syncs with peers.
   */
  async joinRoom(room: RoomLike, topic: string): Promise<void> {
    this.#disconnect();
    this.#reset(new Y.Doc());
    this.#activeId = undefined;
    this.#emit();
    await this.#connect(room, topic);
    this.#pickActive();
    if (!this.#activeId) {
      // Wait for peers to send files; if the room stays empty, start one.
      this.#emptyTimer = setTimeout(() => {
        if (this.workspace.list().length === 0 && this.workspace.canWrite)
          this.#placeholderId = this.addFile("Untitled-1");
      }, EMPTY_ROOM_GRACE_MS);
    }
  }

  /** Stops syncing. The current content stays open locally. */
  leaveRoom(): void {
    this.#disconnect();
    this.#emit();
    if (this.#options.persistLocally) this.ready = this.#keepLocally();
  }

  /** After leaving: what's open becomes the stored solo workspace (replacing the old one). */
  async #keepLocally(): Promise<void> {
    const doc = this.doc;
    await forgetDocument(LOCAL_TOPIC);
    if (this.doc !== doc || this.#topic) return;
    this.#persistence = persistDocument(doc, LOCAL_TOPIC);
    await this.#persistence.whenLoaded;
  }

  /**
   * Stops syncing, deletes the locally stored copy of the room, and goes back to this device's
   * own solo workspace (the room's files aren't kept).
   */
  async forgetRoom(topic = this.#topic): Promise<void> {
    this.#disconnect();
    this.#reset(new Y.Doc());
    this.#activeId = undefined;
    this.#emit();
    if (topic) await forgetDocument(topic);
    const seed = this.#options.seed ?? { path: "Untitled-1", content: "" };
    if (this.#options.persistLocally) {
      this.ready = this.#restoreLocal(seed);
      await this.ready;
    } else {
      this.workspace.migrate();
      this.#activeId = this.workspace.create({
        ...seed,
        createdBy: this.#options.profile.clientId,
      });
      this.#emit();
    }
  }

  destroy(): void {
    this.#disconnect();
    this.#unobserve();
    this.#runSessions.destroy();
    this.awareness.destroy();
  }

  async #connect(room: RoomLike, topic: string): Promise<void> {
    this.#disconnect();
    this.#topic = topic;
    this.#persistence = persistDocument(this.doc, topic);
    await this.#persistence.whenLoaded;
    this.workspace.migrate();
    this.#provider = new YjsChannelProvider({ doc: this.doc, awareness: this.awareness, room });
    this.#emit();
  }

  #disconnect(): void {
    clearTimeout(this.#emptyTimer);
    this.#placeholderId = undefined;
    void this.#localMirror?.destroy();
    this.#localMirror = undefined;
    this.#provider?.destroy();
    this.#provider = undefined;
    void this.#persistence?.destroy();
    this.#persistence = undefined;
    this.#topic = undefined;
  }

  #reset(doc: Y.Doc): void {
    this.#unobserve();
    const user = this.awareness?.getLocalState()?.user;
    this.#runSessions?.destroy();
    this.awareness?.destroy();
    this.doc = doc;
    this.workspace = new Workspace(doc);
    this.awareness = new Awareness(doc);
    if (user) this.awareness.setLocalStateField("user", user);
    else this.setProfile(this.#options.profile);
    this.#runSessions = new RunSessions({ workspace: this.workspace, awareness: this.awareness });
    const onSessions = () => {
      this.#sessionList = this.#runSessions.list();
      for (const listener of this.#sessionListeners) listener();
    };
    const offSessions = this.#runSessions.subscribe(onSessions);
    onSessions();
    const offFiles = this.workspace.observeFiles(() => {
      this.#dropPlaceholder();
      this.#pickActive();
      this.#emit();
    });
    // A newer peer may bump the schema version mid-session (→ read-only here).
    const onMeta = () => this.#emit();
    this.workspace.meta.observe(onMeta);
    this.#unobserve = () => {
      offSessions();
      offFiles();
      this.workspace.meta.unobserve(onMeta);
    };
  }

  /**
   * The room's files arrived after all (a slow connection, e.g. through a relay): the untitled
   * file made meanwhile goes, unless something was typed into it.
   */
  #dropPlaceholder(): void {
    const id = this.#placeholderId;
    if (!id) return;
    const placeholder = this.workspace.get(id);
    if (!placeholder) {
      this.#placeholderId = undefined;
      return;
    }
    if (this.workspace.list().length < 2 || placeholder.text.length > 0) return;
    this.#placeholderId = undefined;
    // Outside the observed transaction.
    queueMicrotask(() => {
      if (!this.workspace.get(id)) return;
      this.workspace.delete(id);
      if (this.#activeId === id) this.#activeId = undefined;
      this.#pickActive();
      this.#emit();
    });
  }

  /** Keeps a valid file open: the current one if it still exists, else the first. */
  #pickActive(): void {
    if (this.#activeId && this.workspace.get(this.#activeId)) return;
    const first = this.workspace.list()[0];
    if (first) clearTimeout(this.#emptyTimer);
    this.#activeId = first?.id;
  }

  #uniquePath(path: string): string {
    if (!this.workspace.findByPath(path)) return path;
    const dot = path.lastIndexOf(".");
    const [stem, ext] = dot > 0 ? [path.slice(0, dot), path.slice(dot)] : [path, ""];
    for (let n = 2; ; n++) {
      const candidate = `${stem} (${n})${ext}`;
      if (!this.workspace.findByPath(candidate)) return candidate;
    }
  }

  #takeSnapshot(): WorkspaceSnapshot {
    return {
      files: this.workspace.list().map((f) => ({ id: f.id, path: f.path })),
      activeId: this.#activeId,
      canWrite: this.workspace.canWrite,
      toolchains: this.workspace.toolchains(),
    };
  }

  #emit(): void {
    this.#snapshot = this.#takeSnapshot();
    for (const listener of this.#listeners) listener();
  }
}
